import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";

import DefaultColors from "./DefaultColors";

import FormInput from "@/components/form-fields/form-input";
import FormDialog from "@/components/reusables/FormDialog";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import { Button } from "@/components/ui/button";
import { ColorPicker } from "@/components/ui/color-picker";
import { DialogFooter } from "@/components/ui/dialog";
import { Form, FormField } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TWENTY } from "@/constants/constants";
import { formatToCapitalizedWords } from "@/lib/utils";
import { AllForOptions, TOptions, TypeTOptions } from "@/schemas/GlobalSchema";

export const handleDetailsErrors = (
  errors: FieldErrors<TypeTOptions>,
): string => {
  let errorMsg: string = "";

  if (errors.details) {
    const detailsError = errors.details;
    if (detailsError.position) {
      errorMsg = detailsError.position.message ?? "";
    } else if (detailsError.textColor) {
      errorMsg = detailsError.textColor.message ?? "";
    } else if (detailsError.textOpacity) {
      errorMsg = detailsError.textOpacity.message ?? "";
    } else if (detailsError.backgroundColor) {
      errorMsg = detailsError.backgroundColor.message ?? "";
    } else if (detailsError.backgroundOpacity) {
      errorMsg = detailsError.backgroundOpacity.message ?? "";
    }
  }

  return errorMsg;
};

type TLabelsProps<T> = {
  title?: string;
  isOpen: boolean;
  toggleModal: () => void;
  optionFor: AllForOptions;
  position: number;
  addOption: (data: T) => Promise<void>;
};

const AddOption = <T,>({
  title,
  isOpen,
  toggleModal,
  optionFor,
  position,
  addOption,
}: TLabelsProps<T>): React.JSX.Element => {
  const form = useForm<TypeTOptions>({
    resolver: zodResolver(TOptions),
    values: {
      name: "",
      for: optionFor,
      details: {
        position,
        textColor: "",
        textOpacity: 100,
        backgroundColor: "",
        backgroundOpacity: 12,
      },
    },
  });

  const handleExit = (): void => {
    form.reset();
    toggleModal();
  };

  const onSubmit = async (data: TypeTOptions): Promise<void> => {
    await addOption(data as T);
    handleExit();
  };

  const [custom, setCustom] = useState(false);

  return (
    <FormDialog
      title={
        title
          ? formatToCapitalizedWords(title)
          : formatToCapitalizedWords(optionFor)
      }
      className='w-[500px] capitalize'
      isOpen={isOpen}
      toggleModal={handleExit}
    >
      <Form {...form}>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit(onSubmit)();
          }}
          className='space-y-4'
        >
          <FormInput
            form={form}
            name='name'
            label='Label Name'
            required
            charLimit={TWENTY}
            input={{
              type: "text",
            }}
          />
          <div>
            <Label className='mb-1 font-normal' required>
              Colors
            </Label>
            <DefaultColors
              colorHandler={(color: string) => {
                form.setValue(`details.textColor`, color, {
                  shouldDirty: true,
                });
                form.setValue(`details.backgroundColor`, color, {
                  shouldDirty: true,
                });
                form.setValue(`details.textOpacity`, 100, {
                  shouldDirty: true,
                });
                form.setValue(`details.backgroundOpacity`, 12, {
                  shouldDirty: true,
                });
              }}
              activeColor={form.watch(`details.textColor`)}
            />
          </div>

          <div className='flex items-center justify-between'>
            <Label>Custom</Label>
            <Switch
              checked={custom}
              onCheckedChange={() => setCustom(!custom)}
            />
          </div>

          {custom && (
            <div className='grid grid-cols-2 gap-4'>
              <div className='p-2 border rounded-md'>
                <FormField
                  name={`details.textColor`}
                  control={form.control}
                  render={({ field }) => (
                    <FormWrapper label='Text'>
                      <ColorPicker
                        onChange={(v) => {
                          form.setValue(`details.textColor`, v);
                        }}
                        onOpacityChange={(v) => {
                          form.setValue(`details.textOpacity`, v);
                        }}
                        value={{
                          color: field.value,
                          opacity: form.watch(`details.textOpacity`),
                        }}
                      />
                    </FormWrapper>
                  )}
                />
              </div>
              <div className='p-2 border rounded-md'>
                <FormField
                  name={`details.backgroundColor`}
                  control={form.control}
                  render={({ field }) => (
                    <FormWrapper label='Background'>
                      <ColorPicker
                        onChange={(v) => {
                          form.setValue(`details.backgroundColor`, v, {
                            shouldDirty: true,
                          });
                        }}
                        onOpacityChange={(v) => {
                          form.setValue(`details.backgroundOpacity`, v, {
                            shouldDirty: true,
                          });
                        }}
                        value={{
                          color: field.value,
                          opacity: form.watch(`details.backgroundOpacity`),
                        }}
                      />
                    </FormWrapper>
                  )}
                />
              </div>
            </div>
          )}

          {form.formState.errors.details && (
            <p className='text-xs text-red-500'>
              {handleDetailsErrors(form.formState.errors)}
            </p>
          )}

          <DialogFooter>
            <Button type='submit'>
              Save{" "}
              {title
                ? formatToCapitalizedWords(title)
                : formatToCapitalizedWords(optionFor)}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AddOption;
